import React from 'react';
import { Link } from 'react-router-dom';
import '../CSS/Products.css';
import { SUITE, PRODUCTS, PRINCIPLES, CATALYST_FAQ } from '../Data/catalyst';
import Reveal from './Reveal';

const pad = (n: number) => `0${n}`.slice(-2);
const delay = (i: number) => ({ ['--i' as string]: i } as React.CSSProperties);

const Products: React.FC = () => {
  return (
    <div className="products-page">
      {/* Banner */}
      <div className="products_banner">
        <div className="products-banner-overlay"></div>
        <div className="products-banner-content">
          <h1 className="products-banner-title">Products</h1>
        </div>
      </div>

      {/* Suite introduction with animated flow */}
      <div className="products-intro">
        <Reveal className="products-intro-content">
          <img className="products-suite-logo" src={SUITE.logo} alt={SUITE.logoAlt} />
          <h2 className="products-intro-title">{SUITE.tagline}</h2>
          <p className="products-intro-text">{SUITE.description}</p>
          <div className="sv-flow products-flow-strip" aria-hidden="true" style={{ ['--n' as string]: SUITE.flow.length } as React.CSSProperties}>
            {SUITE.flow.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="sv-line" style={delay(i)}></span>}
                <span className={`sv-node${i === SUITE.flow.length - 1 ? ' sv-node-last' : ''}`} style={delay(i)}>
                  {label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Product overview cards */}
      <div className="products-overview">
        <Reveal className="products-overview-content">
          {PRODUCTS.map((product, i) => (
            <a key={product.id} href={`#${product.id}`} className="products-overview-card" style={delay(i)}>
              <img className="products-overview-logo" src={product.logo} alt={product.logoAlt} />
              <p className="products-overview-text">{product.suiteBlurb}</p>
              <span className="products-overview-link">{`Explore ${product.name}`}</span>
            </a>
          ))}
        </Reveal>
      </div>

      {/* One section per product */}
      {PRODUCTS.map((product, index) => (
        <section
          key={product.id}
          id={product.id}
          className={`product-section ${index % 2 === 0 ? 'product-section-tint' : 'product-section-white'}`}
        >
          <Reveal className="product-inner">
            <div className="product-head">
              <div className="product-head-text">
                <img className="product-logo" src={product.logo} alt={product.logoAlt} />
                <p className="product-strapline">{product.strapline}</p>
                <h2 className="product-name">{product.name}</h2>
                <p className="product-headline">{product.headline}</p>
                <p className="product-summary">{product.summary}</p>
              </div>
              <div className="product-built-for">
                <h3 className="product-built-for-title">Built for</h3>
                <ul className="product-built-for-list">
                  {product.builtFor.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="product-cards">
              {product.capabilities.map((cap, i) => (
                <div className="product-card" key={cap.title} style={delay(i)}>
                  <span className="product-card-number">{pad(i + 1)}</span>
                  <h3 className="product-card-title">{cap.title}</h3>
                  <p className="product-card-text">{cap.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </section>
      ))}

      {/* Principles */}
      <div className="products-principles">
        <Reveal className="products-principles-content">
          <h2 className="products-section-title">Evidence-Linked, With Human Review</h2>
          <div className="products-principles-cards">
            {PRINCIPLES.map((item, i) => (
              <div className="product-card" key={item.title} style={delay(i)}>
                <h3 className="product-card-title">{item.title}</h3>
                <p className="product-card-text">{item.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* FAQ */}
      <div className="products-faq">
        <Reveal className="products-faq-content">
          <h2 className="products-section-title">Frequently Asked Questions</h2>
          <div className="products-faq-list">
            {CATALYST_FAQ.map((item) => (
              <details className="products-faq-item" key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Call to action */}
      <div className="products-cta">
        <div className="products-cta-content">
          <h2 className="products-cta-title">Explore the Catalyst Suite</h2>
          <div className="products-cta-actions">
            <Link to="/contact/" className="products-btn products-btn-light">
              Contact Us
            </Link>
            <a href={`mailto:${SUITE.email}`} className="products-cta-mail">
              {SUITE.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
